<template>
  <a-drawer :open="visible" :title="`节点 #${proxy?.proxy_id || ''}`" width="720" @close="$emit('update:visible', false)">
    <template v-if="proxy">
      <a-tabs v-model:activeKey="activeTab" size="small">
        <a-tab-pane key="info" tab="节点信息">
          <a-descriptions :column="2" bordered size="small" style="margin-bottom:16px">
            <a-descriptions-item label="大区">
              <a-space :size="4" wrap>
                <a-tag v-for="(name, i) in (proxy.region_names || [proxy.region_name])" :key="i" color="blue">{{ name }}</a-tag>
              </a-space>
            </a-descriptions-item>
            <a-descriptions-item label="服务器">{{ proxy.node_name }}</a-descriptions-item>
            <a-descriptions-item label="所属用户" :span="2">
              <span v-if="proxy.proxy_user_id">
                {{ proxy.proxy_user_wechat_nickname || `#${proxy.proxy_user_id}` }}
                <span v-if="proxy.proxy_user_wechat_id" style="color:#6b7280"> · {{ proxy.proxy_user_wechat_id }}</span>
              </span>
              <span v-else style="color:#9ca3af">未归属</span>
              <a-button v-if="!editingAssignment" size="small" type="link" @click="startEditAssignment">修改</a-button>
            </a-descriptions-item>
            <a-descriptions-item label="端口">
              {{ proxy.port }}
              <a-button size="small" type="link" @click="copyField(String(proxy.port), '端口')">复制</a-button>
            </a-descriptions-item>
            <a-descriptions-item label="账号">
              <span style="font-family:ui-monospace,Menlo,monospace">{{ proxy.username }}</span>
              <a-button size="small" type="link" @click="copyField(proxy.username, '账号')">复制</a-button>
            </a-descriptions-item>
            <a-descriptions-item label="密码">
              <span style="font-family:ui-monospace,Menlo,monospace">{{ proxy.password }}</span>
              <a-button size="small" type="link" @click="copyField(proxy.password, '密码')">复制</a-button>
            </a-descriptions-item>
            <a-descriptions-item label="状态">
              <a-tag :color="statusColor">{{ proxy.status }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="到期">
              {{ formatDateTime(proxy.expire_at) }}
              <a-button size="small" type="link" @click="copyField(formatDateTime(proxy.expire_at), '到期')">复制</a-button>
            </a-descriptions-item>
            <a-descriptions-item label="当前连接数">
              <span :style="{ color: (proxy.active_clients || 0) > 0 ? '#1677ff' : '#9ca3af', fontWeight: 600 }">
                {{ proxy.active_clients || 0 }}
              </span>
            </a-descriptions-item>
            <a-descriptions-item label="子进程状态">
              <a-tag :color="childRunning ? 'green' : 'orange'">
                {{ childRunning ? '运行中' : '等待重建' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="子进程 PID">
              <span v-if="childRunning" style="font-family:ui-monospace,Menlo,monospace">{{ proxy.child_pid }}</span>
              <span v-else style="color:#9ca3af">—</span>
            </a-descriptions-item>
            <a-descriptions-item label="子进程版本">
              <a-tag color="purple" style="margin:0">{{ proxy.child_binary_version || 'unknown' }}</a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="重启次数">
              {{ proxy.child_restart_count || 0 }} 次
            </a-descriptions-item>
            <a-descriptions-item label="子进程启动时间">
              {{ formatChildStartedAt(proxy.child_started_at) }}
            </a-descriptions-item>
            <a-descriptions-item label="子进程运行时长">
              {{ formatChildUptime(proxy.child_started_at) }}
            </a-descriptions-item>
            <a-descriptions-item label="上行">{{ formatBytes(proxy.bytes_up) }}</a-descriptions-item>
            <a-descriptions-item label="下行">{{ formatBytes(proxy.bytes_down) }}</a-descriptions-item>
            <a-descriptions-item label="创建时间">{{ formatDateTime(proxy.created_at) }}</a-descriptions-item>
          </a-descriptions>

          <div v-if="editingAssignment" style="margin-bottom:16px;padding:12px;border:1px solid #e5e7eb;border-radius:6px">
            <div style="color:#6b7280;font-size:13px;margin-bottom:8px">修改所属用户</div>
            <ProxyUserPicker v-model="editProxyUserID" />
            <a-space style="margin-top:10px">
              <a-button size="small" @click="cancelEditAssignment">取消</a-button>
              <a-button size="small" type="primary" :loading="savingAssignment" @click="saveAssignment">保存归属</a-button>
              <a-button
                v-if="proxy.proxy_user_id"
                size="small"
                danger
                :loading="savingAssignment"
                @click="confirmClearAssignment"
              >清空归属</a-button>
            </a-space>
          </div>

          <div style="margin-bottom:16px">
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px">
              <span style="color:#6b7280;font-size:13px">备注</span>
              <a-button
                v-if="!editingNote"
                size="small"
                type="link"
                @click="startEditNote"
              >{{ proxy.note ? '编辑' : '添加' }}</a-button>
              <a-space v-else size="small">
                <a-button size="small" @click="cancelEditNote">取消</a-button>
                <a-button size="small" type="primary" :loading="savingNote" @click="saveNote">保存</a-button>
              </a-space>
            </div>
            <div v-if="!editingNote" style="color:#374151;min-height:22px;white-space:pre-wrap">
              {{ proxy.note || '—' }}
            </div>
            <a-textarea
              v-else
              ref="noteTextarea"
              v-model:value="noteDraft"
              :rows="3"
              :maxlength="512"
              placeholder="例: 张三 - 网通一区主号 - 单号 2026-001"
            />
          </div>

          <a-space style="margin-bottom:16px">
            <a-button type="primary" @click="copyAll">复制 5 字段</a-button>
            <a-button @click="copyByLines">按行复制</a-button>
            <a-button
              type="primary"
              :disabled="proxy.status === 'deleted' || proxy.status === 'creating'"
              @click="extendModalOpen = true"
            >{{ proxy.status === 'expired' ? '重新激活' : '调整时间' }}</a-button>
          </a-space>

          <TrafficChart :proxy-id="proxy.proxy_id" />
        </a-tab-pane>

        <a-tab-pane key="plugins" tab="插件">
          <a-alert
            type="info"
            show-icon
            message="插件只对当前节点生效；启用自动吃药时会自动启用角色信息依赖。"
            style="margin-bottom:16px"
          />
          <a-spin :spinning="pluginsLoading">
            <div class="plugin-list">
              <a-card v-for="plugin in plugins" :key="plugin.key" size="small" class="plugin-item">
                <div class="plugin-item-head">
                  <div>
                    <strong>{{ plugin.name }}</strong>
                    <a-tag :color="plugin.enabled ? 'green' : 'default'" style="margin-left:8px">
                      {{ plugin.enabled ? '已启用' : '未启用' }}
                    </a-tag>
                  </div>
                  <a-switch :checked="plugin.enabled" :loading="pluginSaving === plugin.key" @change="togglePlugin(plugin, $event)" />
                </div>
                <p>{{ plugin.description }}</p>
                <small v-if="plugin.depends_on.length">依赖：{{ pluginDependencyNames(plugin) }}</small>
              </a-card>
            </div>
          </a-spin>
        </a-tab-pane>

        <a-tab-pane :tab="`实时连接 ${connections.length}`" key="connections">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
            <span style="color:#9ca3af;font-size:11px">每 5 秒自动刷新</span>
          </div>
          <a-table
            :columns="connColumns"
            :dataSource="connections"
            :pagination="false"
            rowKey="id"
            size="small"
            :scroll="{ x: 'max-content' }"
            :locale="{ emptyText: proxy.status === 'active' ? '当前没有活跃连接' : '节点未激活' }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'source'">
                <span style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.source_ip }}{{ record.source_port ? ':' + record.source_port : '' }}
                </span>
              </template>
              <template v-if="column.key === 'account'">
                <span v-if="record.game_stats?.account" style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.game_stats.account }}
                </span>
                <span v-else style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'source_location'">
                <span v-if="record.source_location" :title="`${record.source_ip} · ${record.source_location}`">{{ formatSourceLocation(record.source_location) }}</span>
                <span v-else :title="record.source_ip" style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'role_name'">
                <button v-if="record.game_stats?.role_name" type="button" class="role-detail-link" :title="`来源 ${record.source_ip}${record.source_port ? ':' + record.source_port : ''}`" @click.stop="openRoleDetail(record)">{{ record.game_stats.role_name }}</button>
                <span v-else :title="`来源 ${record.source_ip}${record.source_port ? ':' + record.source_port : ''}`" style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'level'">
                <a-tag v-if="record.game_stats?.role_name" color="blue" style="margin:0">
                  {{ record.game_stats.level }} 级
                </a-tag>
                <span v-else style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'target'">
                <span style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.target_ip || '—' }}{{ record.target_port ? ':' + record.target_port : '' }}
                </span>
              </template>
              <template v-if="column.key === 'game_server'">
                <span v-if="record.game_server" :title="`目标 ${record.target_ip || '—'}${record.target_port ? ':' + record.target_port : ''}`">{{ record.game_server }}</span>
                <span v-else :title="`目标 ${record.target_ip || '—'}${record.target_port ? ':' + record.target_port : ''}`" style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'map'">
                <span v-if="record.map_name">{{ record.map_name }}</span>
                <span v-else-if="record.map_id" style="font-family:ui-monospace,Menlo,monospace">#{{ record.map_id }}</span>
                <span v-else style="color:#9ca3af">—</span>
              </template>
              <template v-if="column.key === 'duration'">
                {{ formatDuration(record.started_at) }}
              </template>
              <template v-if="column.key === 'bytes'">
                <div class="traffic-metric">
                  ↑ {{ formatBytes(record.bytes_up) }} / ↓ {{ formatBytes(record.bytes_down) }}
                </div>
                <div class="traffic-metric traffic-rate">
                  ↑ {{ formatRate(record.up_bps) }} / ↓ {{ formatRate(record.down_bps) }}
                </div>
              </template>
              <template v-if="column.key === 'synthetic_heartbeats_up'">
                <span v-if="record.synthetic_heartbeats_up" style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.synthetic_heartbeats_up }}
                </span>
                <span v-else style="color:#9ca3af">0</span>
              </template>
              <template v-if="column.key === 'connection_health'">
                <a-tooltip placement="topRight">
                  <template #title>
                    <div class="connection-health-detail">
                      <div v-for="item in connectionHealth(record)" :key="item.key">
                        <strong>{{ item.label }}</strong><span v-if="item.latency"> · {{ item.latency }}</span>
                        <div>{{ item.detail }}</div>
                      </div>
                    </div>
                  </template>
                  <div class="connection-health-compact">
                    <a-tag :color="connectionHealthSummary(record).color" style="margin:0">
                      {{ connectionHealthSummary(record).label }}
                    </a-tag>
                    <span v-if="connectionHealthSummary(record).latency" class="connection-health-latency">
                      {{ connectionHealthSummary(record).latency }}
                    </span>
                  </div>
                </a-tooltip>
              </template>
              <template v-if="column.key === 'inventory'">
                <a-button size="small" type="link" :disabled="!record.game_stats?.inventory?.length" @click="openInventory(record)">
                  背包 {{ record.game_stats?.inventory?.length || 0 }}
                </a-button>
              </template>
            </template>
          </a-table>
          <RoleInfoDrawer v-model:open="roleInfoOpen" :connection="selectedRoleConn" />
          <a-modal v-model:open="inventoryOpen" :title="`${selectedInventoryConn?.game_stats?.role_name || '角色'} · 背包与自动吃药`" :footer="null" width="860px">
            <a-alert v-if="!autoPotionPluginEnabled" type="info" show-icon message="该节点尚未启用自动吃药插件，请在当前节点详情的插件页开启。" style="margin-bottom:12px" />
            <template v-if="autoPotionPluginEnabled">
            <a-alert type="info" show-icon message="按服务器返回的实时血量、蓝量判断；只会使用当前背包中选中的药品。" style="margin-bottom:12px" />
            <div class="resource-summary">
              <div><span>当前血量</span><strong class="hp-value">{{ selectedInventoryConn?.game_stats?.hp || 0 }} / {{ selectedInventoryConn?.game_stats?.max_hp || 0 }}</strong><a-tag v-if="selectedInventoryConn?.game_stats?.is_dead" color="red" style="margin-left:8px">角色已死亡</a-tag></div>
              <div><span>当前蓝量</span><strong class="mp-value">{{ selectedInventoryConn?.game_stats?.mp || 0 }} / {{ selectedInventoryConn?.game_stats?.max_mp || 0 }}</strong></div>
            </div>
            <div class="potion-config">
              <div class="potion-config-title"><strong>自动吃药</strong><a-switch v-model:checked="potionRule.enabled" checked-children="开启" un-checked-children="关闭" /></div>
              <div class="potion-config-grid">
                <span>血量低于</span><a-input-number v-model:value="potionRule.hp_threshold" :min="1" :max="100" addon-after="%" />
                <a-select v-model:value="potionRule.hp_item_id" placeholder="选择红药" style="min-width:220px" :options="hpPotionOptions" />
                <span>蓝量低于</span><a-input-number v-model:value="potionRule.mp_threshold" :min="1" :max="100" addon-after="%" />
                <a-select v-model:value="potionRule.mp_item_id" placeholder="选择蓝药" style="min-width:220px" :options="mpPotionOptions" />
              </div>
              <div class="potion-config-actions">
                <span v-if="selectedInventoryConn?.game_stats?.auto_potion_requests" class="potion-status">已自动使用 {{ selectedInventoryConn.game_stats.auto_potion_requests }} 次，最近：{{ selectedInventoryConn.game_stats.last_auto_potion_item }}</span>
                <a-space>
                  <a-button :loading="testingPotion === 'hp'" :disabled="!potionRule.hp_item_id || selectedInventoryConn?.game_stats?.is_dead" @click="testPotion('hp')">测试吃红药</a-button>
                  <a-button :loading="testingPotion === 'mp'" :disabled="!potionRule.mp_item_id || selectedInventoryConn?.game_stats?.is_dead" @click="testPotion('mp')">测试吃蓝药</a-button>
                  <a-button type="primary" :loading="savingPotion" @click="savePotionRule">保存设置</a-button>
                </a-space>
              </div>
            </div>
            </template>
            <a-table :columns="inventoryColumns" :dataSource="selectedInventoryConn?.game_stats?.inventory || []" rowKey="slot" size="small" :pagination="false" :scroll="{ y: 420 }">
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'slot'">{{ record.slot }}</template>
                <template v-if="column.key === 'name'">{{ record.item_name || `物品 #${record.item_id}` }}</template>
                <template v-if="column.key === 'attribute'"><span v-if="record.attribute_string" style="color:#722ed1">{{ record.attribute_string }}</span><span v-else style="color:#9ca3af">—</span></template>
              </template>
            </a-table>
          </a-modal>
        </a-tab-pane>

        <a-tab-pane :tab="`掉线记录 ${auditTotal}`" key="audits">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px">
            <span style="color:#9ca3af;font-size:11px">端口</span>
            <a-input-number
              v-model:value="auditPortMin"
              :min="0"
              :max="65535"
              size="small"
              style="width:90px"
              placeholder="最小"
              :title="'target_port 下限，默认 13000'"
            />
            <span style="color:#9ca3af">—</span>
            <a-input-number
              v-model:value="auditPortMax"
              :min="0"
              :max="65535"
              size="small"
              style="width:90px"
              placeholder="最大"
              :title="'target_port 上限，默认 13020'"
            />
            <a-button size="small" type="primary" @click="reloadAudits(0)" :loading="auditsLoading">应用</a-button>
            <a-button size="small" @click="resetAuditFilters" :title="'重置为默认: 端口 13000-13020'">重置</a-button>
            <span style="color:#9ca3af;font-size:11px;margin-left:auto">
              共 {{ auditTotal }} 条 · 每 5 秒自动刷新
            </span>
          </div>
          <div
            v-if="auditSummaryTotal > 0"
            style="border:1px solid #eef2f7;background:#f8fafc;border-radius:6px;padding:10px;margin-bottom:10px"
          >
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;margin-bottom:8px">
              <strong style="font-size:13px">最新 {{ auditSummaryTotal }} 条汇总</strong>
              <span style="color:#9ca3af;font-size:11px">按当前端口筛选统计，最多取最新 200 条</span>
            </div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px">
              <a-tag
                v-for="item in auditResponsibilitySummary"
                :key="item.key"
                :color="responsibilityColor(item.key)"
                style="margin:0"
              >
                {{ responsibilityLabel(item.key) }} {{ item.count }} 条 / {{ formatPercent(item.count, auditSummaryTotal) }}
              </a-tag>
            </div>
            <div style="display:flex;gap:6px;flex-wrap:wrap">
              <a-tooltip
                v-for="item in auditCauseSummary"
                :key="item.key"
                placement="topLeft"
              >
                <template #title>
                  <div style="max-width:360px;line-height:1.6">
                    <div><strong>{{ causeLabel(item.key) }}</strong></div>
                    <div>{{ causeExplanation(item.key, item.count) }}</div>
                  </div>
                </template>
                <a-tag :color="responsibilityColor(causeResponsibility(item.key))" style="margin:0;cursor:help">
                  {{ causeLabel(item.key) }} {{ item.count }} 条 / {{ formatPercent(item.count, auditSummaryTotal) }}
                </a-tag>
              </a-tooltip>
            </div>
          </div>
          <a-table
            :columns="auditColumns"
            :dataSource="audits"
            rowKey="id"
            size="small"
            :pagination="auditPagination"
            @change="onAuditTableChange"
            :customRow="auditCustomRow"
            :locale="{ emptyText: '暂无掉线审计事件' }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'target'">
                <span style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.source_ip }} → {{ record.target_ip || '—' }}{{ record.target_port ? ':' + record.target_port : '' }}
                </span>
              </template>
              <template v-if="column.key === 'role'">
                <div>{{ record.role_name || '未识别角色' }}</div>
                <div style="font-size:11px;color:#9ca3af">{{ record.account || '—' }}</div>
              </template>
              <template v-if="column.key === 'responsibility'">
                <a-tag :color="responsibilityColor(record.responsibility)" style="margin:0">
                  {{ responsibilityLabel(record.responsibility) }}
                </a-tag>
              </template>
              <template v-if="column.key === 'reason'">
                <a-tooltip placement="topLeft">
                  <template #title>
                    <div style="max-width:360px;line-height:1.6">
                      <div><strong>{{ causeLabel(record.cause_code) }}</strong></div>
                      <div>{{ causeExplanation(record.cause_code, record.correlated_count || 1) }}</div>
                      <div style="margin-top:6px">关键证据：{{ causeEvidence(record) }}</div>
                      <div style="margin-top:6px;color:#d1d5db">
                        TCP观察侧：{{ closeSideTooltip(record.close_side) }}
                      </div>
                      <div style="color:#d1d5db">原始原因：{{ record.close_reason || 'unknown' }}</div>
                      <div v-if="record.error_detail" style="color:#d1d5db">
                        错误明细：{{ record.error_detail }}
                      </div>
                    </div>
                  </template>
                  <span style="display:inline-flex;align-items:center;gap:6px;cursor:help">
                    <span>{{ causeLabel(record.cause_code) }}</span>
                  </span>
                </a-tooltip>
              </template>
              <template v-if="column.key === 'duration_ms'">
                {{ formatDurationMs(record.duration_ms) }}
              </template>
              <template v-if="column.key === 'bytes'">
                <span style="font-family:ui-monospace,Menlo,monospace">
                  ↑ {{ formatBytes(record.bytes_up) }} / ↓ {{ formatBytes(record.bytes_down) }}
                </span>
              </template>
              <template v-if="column.key === 'synthetic_heartbeats_up'">
                <span v-if="record.synthetic_heartbeats_up" style="font-family:ui-monospace,Menlo,monospace">
                  {{ record.synthetic_heartbeats_up }}
                </span>
                <span v-else style="color:#9ca3af">0</span>
              </template>
              <template v-if="column.key === 'ended_at'">
                <span :title="record.error_detail || ''">{{ formatAuditTime(record.ended_at) }}</span>
              </template>
            </template>
          </a-table>
          <a-modal v-model:open="auditDetailOpen" title="掉线责任详情" :footer="null" width="680px">
            <a-descriptions v-if="selectedAudit" bordered size="small" :column="2">
              <a-descriptions-item label="一级责任">
                <a-tag :color="responsibilityColor(selectedAudit.responsibility)">{{ responsibilityLabel(selectedAudit.responsibility) }}</a-tag>
              </a-descriptions-item>
              <a-descriptions-item label="二级原因" :span="2">{{ causeLabel(selectedAudit.cause_code) }}</a-descriptions-item>
              <a-descriptions-item label="玩家会看到的说明" :span="2">
                <strong>{{ playerText(selectedAudit.cause_code).label }}</strong>
                <div>{{ playerText(selectedAudit.cause_code).detail }}</div>
              </a-descriptions-item>
              <a-descriptions-item v-if="selectedAudit.verdict_text" label="单连接判定" :span="2">
                <div>{{ selectedAudit.verdict_text }}</div>
                <div v-if="selectedAudit.first_stall_at_ms" style="color:#6b7280">
                  实际断开时刻：{{ new Date(selectedAudit.first_stall_at_ms).toLocaleTimeString('zh-CN', { hour12: false }) }}
                </div>
              </a-descriptions-item>
              <a-descriptions-item v-if="linkEvidenceLines(selectedAudit.evidence).length" label="链路证据" :span="2">
                <div v-for="line in linkEvidenceLines(selectedAudit.evidence)" :key="line">{{ line }}</div>
              </a-descriptions-item>
              <a-descriptions-item label="账号">{{ selectedAudit.account || '—' }}</a-descriptions-item>
              <a-descriptions-item label="角色">{{ selectedAudit.role_name || '—' }}</a-descriptions-item>
              <a-descriptions-item label="游戏服">{{ selectedAudit.game_server || selectedAudit.target_ip || '—' }}</a-descriptions-item>
              <a-descriptions-item label="地图">{{ selectedAudit.map_name || '—' }}</a-descriptions-item>
              <a-descriptions-item label="用户本地网络心跳">{{ evidenceClientText(selectedAudit) }}</a-descriptions-item>
              <a-descriptions-item label="Agent主动请求">{{ evidenceAgentText(selectedAudit) }}</a-descriptions-item>
              <a-descriptions-item label="游戏服务器原生探测" :span="2">{{ evidenceServerText(selectedAudit) }}</a-descriptions-item>
              <a-descriptions-item label="关联影响" :span="2">同节点服务器同期 {{ selectedAudit.correlated_count || 1 }} 条连接结束</a-descriptions-item>
              <a-descriptions-item label="TCP技术信息" :span="2">
                {{ closeSideLabel(selectedAudit.close_side) }} · {{ closeReasonLabel(selectedAudit.close_reason) }}
                <span v-if="selectedAudit.error_detail"> · {{ selectedAudit.error_detail }}</span>
              </a-descriptions-item>
            </a-descriptions>
          </a-modal>
        </a-tab-pane>
      </a-tabs>

      <ExtendProxyModal
        v-model:visible="extendModalOpen"
        :proxies="proxy ? [proxy] : []"
        :proxy-id="proxy?.proxy_id ?? null"
        @success="onExtendSuccess"
      />
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted, nextTick, ref } from 'vue';
import { message, Modal } from 'ant-design-vue';
import TrafficChart from './TrafficChart.vue';
import ExtendProxyModal from './ExtendProxyModal.vue';
import ProxyUserPicker from './ProxyUserPicker.vue';
import RoleInfoDrawer from './RoleInfoDrawer.vue';
import { getProxyAutoPotion, getProxyConnections, listProxyConnectionAudits, patchProxyNote, patchProxyUserAssignment, saveProxyAutoPotion, testProxyAutoPotion, type AutoPotionRule } from '../api/proxies';
import { getProxyUser } from '../api/proxyUsers';
import { resolveNodeAddress } from '../api/nodes';
import { listPlugins, setProxyPlugin, type NodePlugin } from '../api/plugins';
import { disconnectCauseExplanation, disconnectCauseLabel, disconnectPlayerText, disconnectResponsibilityLabel, filterAuditColumnsForCharacterPlugin, linkEvidenceLines } from './disconnectPresentation';
import type { LiveConn, ProxyConnectionAuditEvent, ProxyItem } from '../types';
import { formatTrafficBytes, formatTrafficRate } from '../utils/trafficFormat';
import { formatSourceLocation } from './sourceLocation';
import { formatAuditTime } from './auditTime';

const props = defineProps<{ visible: boolean; proxy: ProxyItem | null }>();
const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'updated'): void;
}>();

// Note edit state. When editingNote is true the textarea is shown
// and a Save / Cancel pair appears; the displayed value is replaced
// with the textarea bound to noteDraft. On save we call PATCH
// /api/proxies/{id} and update the bound `proxy` object in place so
// the rest of the drawer (and the list, on next refresh) sees the
// updated value without a full reload.
const editingNote = ref(false);
const noteDraft = ref('');
const savingNote = ref(false);
const noteTextarea = ref();
// Extend-modal visibility. The button above flips this; the modal
// emits `success` and we mutate `props.proxy.expire_at` in place.
const extendModalOpen = ref(false);
const activeTab = ref('info');
const plugins = ref<NodePlugin[]>([]);
const pluginsLoading = ref(false);
const pluginSaving = ref('');
const editingAssignment = ref(false);
const editProxyUserID = ref<number | null>(null);
const savingAssignment = ref(false);

const startEditAssignment = () => {
  editProxyUserID.value = props.proxy?.proxy_user_id ?? null;
  editingAssignment.value = true;
};

const cancelEditAssignment = () => {
  editProxyUserID.value = props.proxy?.proxy_user_id ?? null;
  editingAssignment.value = false;
};

const persistAssignment = async (proxyUserID: number | null) => {
  if (!props.proxy) return;
  savingAssignment.value = true;
  try {
    await patchProxyUserAssignment(props.proxy.proxy_id, proxyUserID);
    props.proxy.proxy_user_id = proxyUserID;
    if (proxyUserID === null) {
      props.proxy.proxy_user_wechat_nickname = null;
      props.proxy.proxy_user_wechat_id = null;
    } else {
      const user = (await getProxyUser(proxyUserID)).data;
      props.proxy.proxy_user_wechat_nickname = user.wechat_nickname;
      props.proxy.proxy_user_wechat_id = user.wechat_id;
    }
    editingAssignment.value = false;
    emit('updated');
    message.success('所属用户已更新');
  } catch (error: any) {
    const code = error?.response?.data?.error?.code;
    if (code === 'PROXY_USER_DISABLED') {
      message.error('该用户已停用，不能接收节点');
    } else {
      message.error(error?.response?.data?.error?.message || '所属用户更新失败');
    }
  } finally {
    savingAssignment.value = false;
  }
};

const confirmClearAssignment = () => {
  Modal.confirm({
    title: '确认清空所属用户？',
    content: '只会清空后台归属，不会重启节点，也不会影响玩家连接。',
    okType: 'danger',
    onOk: () => persistAssignment(null),
  });
};

const saveAssignment = () => {
  if (editProxyUserID.value === null) {
    confirmClearAssignment();
    return;
  }
  void persistAssignment(editProxyUserID.value);
};

const startEditNote = async () => {
  if (!props.proxy) return;
  noteDraft.value = props.proxy.note || '';
  editingNote.value = true;
  // Focus the textarea on next tick so the cursor lands inside.
  await nextTick();
  noteTextarea.value?.focus?.();
};

const cancelEditNote = () => {
  editingNote.value = false;
  noteDraft.value = '';
};

const onExtendSuccess = (payload: { proxy_id: number; expire_at: string; reactivated: boolean; adjustment: 'extend' | 'reduce'; runtime_sync_pending?: boolean }) => {
  if (props.proxy && props.proxy.proxy_id === payload.proxy_id) {
    props.proxy.expire_at = payload.expire_at;
    if (payload.reactivated) props.proxy.status = 'active';
  }
  extendModalOpen.value = false;
  const actionText = payload.reactivated ? '已重新激活，到期时间' : payload.adjustment === 'reduce' ? '已减少时间，到期时间' : '已续费到';
  if (payload.runtime_sync_pending) {
    message.warning(`${actionText} ${payload.expire_at.slice(0, 10)}，已保存，等待节点同步，请勿重复扣减`);
  } else {
    message.success(`${actionText} ${payload.expire_at.slice(0, 10)}`);
  }
  if (payload.reactivated) emit('updated');
};

const saveNote = async () => {
  if (!props.proxy) return;
  const trimmed = noteDraft.value.trim();
  if (trimmed === (props.proxy.note || '')) {
    // No-op: nothing changed. Just exit edit mode.
    editingNote.value = false;
    return;
  }
  savingNote.value = true;
  try {
    await patchProxyNote(props.proxy.proxy_id, trimmed);
    // Mutate the bound proxy in place so the read-only view + list
    // both pick up the new value without reloading the whole drawer.
    props.proxy.note = trimmed || null;
    editingNote.value = false;
    message.success('备注已保存');
  } catch (e: any) {
    message.error(e?.response?.data?.error?.message || '保存失败');
  } finally {
    savingNote.value = false;
  }
};

// Periodically re-fetch the live-connection snapshot while the drawer
// is open so the table stays current. Same cadence as the agent's
// own heartbeat (5s).
let refreshTimer: number | undefined;
const connections = ref<LiveConn[]>([]);
const inventoryOpen = ref(false);
const selectedInventoryConn = ref<LiveConn | null>(null);
const roleInfoOpen = ref(false);
const selectedRoleConn = ref<LiveConn | null>(null);
const openRoleDetail = (record: LiveConn) => {
  selectedRoleConn.value = record;
  roleInfoOpen.value = true;
};
const savingPotion = ref(false);
const characterPluginEnabled = ref(false);
const autoPotionPluginEnabled = ref(false);
const loadPluginState = async () => {
	if (!props.proxy) return;
	pluginsLoading.value = true;
	try { const items=(await listPlugins(props.proxy.proxy_id)).data.items; plugins.value=items; characterPluginEnabled.value=items.some(v=>v.key==='character_monitor'&&v.enabled); autoPotionPluginEnabled.value=items.some(v=>v.key==='auto_potion'&&v.enabled); }
	catch { plugins.value=[]; characterPluginEnabled.value=false; autoPotionPluginEnabled.value=false; }
	finally { pluginsLoading.value = false; }
};
const pluginDependencyNames = (plugin: NodePlugin) => plugin.depends_on.map(key => plugins.value.find(item => item.key === key)?.name || key).join('、');
const togglePlugin = async (plugin: NodePlugin, enabled: boolean) => {
  if (!props.proxy) return;
  pluginSaving.value = plugin.key;
  try { await setProxyPlugin(props.proxy.proxy_id, plugin.key, enabled); await loadPluginState(); message.success(`${plugin.name}已${enabled?'启用':'停用'}`); }
  catch (e:any) { message.error(e?.response?.data?.error?.message || '插件设置失败'); }
  finally { pluginSaving.value = ''; }
};
const testingPotion = ref<'' | 'hp' | 'mp'>('');
const potionRule = ref<AutoPotionRule>({ account: '', role_name: '', enabled: false, hp_threshold: 50, hp_item_id: 0, mp_threshold: 30, mp_item_id: 0 });
const potionItems = computed(() => selectedInventoryConn.value?.game_stats?.inventory || []);
const potionOptions = (filter: (name: string) => boolean) => Array.from(new Map(potionItems.value.filter(v => filter(v.item_name)).map(v => [v.item_id, { value: v.item_id, label: v.item_name }])).values());
const hpPotionOptions = computed(() => potionOptions(name => name.includes('金创药')));
const mpPotionOptions = computed(() => potionOptions(name => name.includes('参')));
const openInventory = async (record: LiveConn) => {
  selectedInventoryConn.value = record;
  potionRule.value = { account: record.game_stats.account || '', role_name: record.game_stats.role_name || '', enabled: false, hp_threshold: 50, hp_item_id: 0, mp_threshold: 30, mp_item_id: 0 };
  inventoryOpen.value = true;
  if (!props.proxy || !autoPotionPluginEnabled.value) return;
  try {
    const { data } = await getProxyAutoPotion(props.proxy.proxy_id);
    const found = data.rules.find(v => (!v.account || v.account === record.game_stats.account) && (!v.role_name || v.role_name === record.game_stats.role_name));
    if (found) potionRule.value = { ...found };
  } catch { message.warning('自动吃药设置加载失败'); }
};
const savePotionRule = async () => {
  if (!props.proxy || !selectedInventoryConn.value) return;
  if (potionRule.value.enabled && (!potionRule.value.hp_item_id || !potionRule.value.mp_item_id)) { message.warning('请同时选择红药和蓝药'); return; }
  savingPotion.value = true;
  try { await saveProxyAutoPotion(props.proxy.proxy_id, potionRule.value); message.success('自动吃药设置已生效'); }
  catch (e: any) { message.error(e?.response?.data?.error?.message || '保存失败'); }
  finally { savingPotion.value = false; }
};
const testPotion = async (kind: 'hp' | 'mp') => {
  if (!props.proxy || !selectedInventoryConn.value) return;
  const itemID = kind === 'hp' ? potionRule.value.hp_item_id : potionRule.value.mp_item_id;
  if (!itemID) return;
  testingPotion.value = kind;
  try {
    await testProxyAutoPotion(props.proxy.proxy_id, { account: selectedInventoryConn.value.game_stats.account, role_name: selectedInventoryConn.value.game_stats.role_name, item_id: itemID, kind });
    message.success(kind === 'hp' ? '已发送测试吃红药请求' : '已发送测试吃蓝药请求');
  } catch (e: any) { message.error(e?.response?.data?.error?.message || '测试请求失败'); }
  finally { testingPotion.value = ''; }
};

// Audit tab state. The page keeps its own filter state so changing
// tabs (info / connections / audits) doesn't reset the operator's
// port selection. Default port range is 13000-13020 (the common
// range for game server frontends; operators typically don't need
// to see e.g. DB ports here).
const audits = ref<ProxyConnectionAuditEvent[]>([]);
const auditSummaryItems = ref<ProxyConnectionAuditEvent[]>([]);
const auditDetailOpen = ref(false);
const selectedAudit = ref<ProxyConnectionAuditEvent | null>(null);
const auditTotal = ref(0);
const auditsLoading = ref(false);
const auditPortMin = ref<number | null>(13000);
const auditPortMax = ref<number | null>(13020);
const auditPage = ref(1);
const auditPageSize = ref(20);
const auditSummaryLimit = 200;

const refreshLiveData = async () => {
  if (!props.visible || !props.proxy) return;
  try {
    const r = await getProxyConnections(props.proxy.proxy_id);
    connections.value = r.data || [];
    if (selectedInventoryConn.value) {
      selectedInventoryConn.value = connections.value.find(v => v.id === selectedInventoryConn.value?.id) || selectedInventoryConn.value;
    }
    if (selectedRoleConn.value) {
      selectedRoleConn.value = connections.value.find(v => v.id === selectedRoleConn.value?.id) || selectedRoleConn.value;
    }
  } catch { /* ignore */ }
  // Only reload audits when the user is actually viewing the
  // audit tab — switching away should not be paying the cost of
  // a filtered query every 5 seconds.
  if (activeTab.value === 'audits') {
    await reloadAudits((auditPage.value - 1) * auditPageSize.value);
  }
};

const reloadAudits = async (offset: number) => {
  if (!props.proxy) return;
  auditsLoading.value = true;
  try {
    const [r, summary] = await Promise.all([
      listProxyConnectionAudits(props.proxy.proxy_id, {
        portMin: auditPortMin.value ?? undefined,
        portMax: auditPortMax.value ?? undefined,
        limit: auditPageSize.value,
        offset,
      }),
      listProxyConnectionAudits(props.proxy.proxy_id, {
        portMin: auditPortMin.value ?? undefined,
        portMax: auditPortMax.value ?? undefined,
        limit: auditSummaryLimit,
        offset: 0,
      }),
    ]);
    audits.value = r.data.items || [];
    auditTotal.value = r.data.total || 0;
    auditSummaryItems.value = summary.data.items || [];
  } catch { /* ignore */ }
  finally {
    auditsLoading.value = false;
  }
};

const resetAuditFilters = () => {
  auditPortMin.value = 13000;
  auditPortMax.value = 13020;
  auditPage.value = 1;
  reloadAudits(0);
};

const onAuditTableChange = (pag: any) => {
  // a-pagination's `change` fires with the new pager; map it to
  // our offset and refetch. The filter state is preserved.
  auditPage.value = pag.current;
  auditPageSize.value = pag.pageSize;
  reloadAudits((pag.current - 1) * pag.pageSize);
};

const auditPagination = computed(() => ({
  current: auditPage.value,
  pageSize: auditPageSize.value,
  total: auditTotal.value,
  showTotal: (t: number) => `共 ${t} 条`,
  showSizeChanger: true,
  pageSizeOptions: ['20', '50', '100'],
  size: 'small' as const,
}));

type AuditSummaryEntry = { key: string; count: number };

const summarizeAuditField = (field: 'responsibility' | 'cause_code'): AuditSummaryEntry[] => {
  const counts = new Map<string, number>();
  for (const item of auditSummaryItems.value) {
    const key = (item[field] || 'unknown').trim() || 'unknown';
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
};

const auditSummaryTotal = computed(() => auditSummaryItems.value.length);
const auditResponsibilitySummary = computed(() => summarizeAuditField('responsibility'));
const auditCauseSummary = computed(() => summarizeAuditField('cause_code'));

const formatPercent = (count: number, total: number) => {
  if (!total) return '0%';
  return `${((count * 100) / total).toFixed(1)}%`;
};

const responsibilityColor = (value: string) => {
  switch (value) {
    case 'node': return 'red';
    case 'server': return 'orange';
    case 'client': return 'blue';
    default: return 'default';
  }
};

const responsibilityLabel = disconnectResponsibilityLabel;
const causeLabel = disconnectCauseLabel;
const playerText = disconnectPlayerText;
const causeExplanation = disconnectCauseExplanation;

const causeResponsibility = (value: string) => {
  if (value.startsWith('node_') || value === 'agent_forwarding_error') return 'node';
  if (value.startsWith('server_')) return 'server';
  if (value.startsWith('client_') || value === 'game_client_unresponsive') return 'client';
  return 'unknown';
};

const ageText = (value?: number) => value === undefined || value < 0 ? '未取得' : `${Math.round(value)}ms前`;
const evidenceClientText = (record: ProxyConnectionAuditEvent) => {
  const e = record.evidence || {};
  return e.client_heartbeats ? `${ageText(e.client_heartbeat_age_ms)}，正常间隔约 ${Math.round(e.client_heartbeat_interval_ms || 0)}ms` : '未取得用户本地网络心跳';
};
const evidenceAgentText = (record: ProxyConnectionAuditEvent) => {
  const e = record.evidence || {};
  if (e.agent_probe_pending && (e.agent_probe_pending_ms || 0) >= 10_000) return `请求超时，已等待 ${Math.round((e.agent_probe_pending_ms || 0) / 1000)}秒`;
  if ((e.agent_probe_response_age_ms ?? -1) >= 0) return `最近成功 ${ageText(e.agent_probe_response_age_ms)}，延迟 ${e.agent_probe_latency_ms || 0}ms`;
  return '尚无主动请求结果';
};
const evidenceServerText = (record: ProxyConnectionAuditEvent) => {
  const e = record.evidence || {};
  if (e.server_probe_pending) return e.server_probe_client_responded
    ? `客户端已响应，服务器尚未确认（等待 ${Math.round((e.server_probe_pending_ms || 0) / 1000)}秒）`
    : `服务器已发起，客户端尚未响应（等待 ${Math.round((e.server_probe_pending_ms || 0) / 1000)}秒）`;
  if ((e.server_probe_ack_age_ms ?? -1) >= 0) return `最近完整确认 ${ageText(e.server_probe_ack_age_ms)}；用户本地网络 ${e.server_probe_client_latency_ms || 0}ms，服务器确认 ${e.server_probe_ack_latency_ms || 0}ms`;
  return '尚无完整原生探测结果';
};
const causeEvidence = (record: ProxyConnectionAuditEvent) => {
  if (record.cause_code === 'node_multi_target_disconnect') return `同节点服务器同期 ${record.correlated_count || 1} 条连接结束`;
  if (record.cause_code === 'server_multi_node_disconnect') return `同一游戏服务器目标跨多台节点服务器同期 ${record.correlated_count || 1} 条连接结束`;
  if (record.cause_code === 'client_multi_node_disconnect') return `同一用户本地网络跨多台节点服务器、多个游戏目标同期 ${record.correlated_count || 1} 条读写错误`;
  if (record.cause_code.startsWith('server_probe_')) return evidenceServerText(record);
  if (record.cause_code === 'server_business_request_timeout') return evidenceAgentText(record);
  if (record.cause_code.startsWith('client_') || record.cause_code === 'game_client_unresponsive') return evidenceClientText(record);
  if (record.cause_code === 'agent_forwarding_error') return record.error_detail || 'Agent报告转发错误';
  return `${closeSideLabel(record.close_side)} · ${closeReasonLabel(record.close_reason)}`;
};
const auditCustomRow = (record: ProxyConnectionAuditEvent) => ({
  onDblclick: () => { selectedAudit.value = record; auditDetailOpen.value = true; },
  style: { cursor: 'pointer' },
});

const startRefresh = () => {
  stopRefresh();
  // Reset to the first page on (re)open. The port range defaults
  // are set in the ref() initializers, so no setup is required
  // before the first reload.
  auditPage.value = 1;
  reloadAudits(0);
  refreshLiveData();
  refreshTimer = window.setInterval(async () => {
    await refreshLiveData();
  }, 5000);
};
const stopRefresh = () => {
  if (refreshTimer) {
    window.clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
};
watch(() => props.visible, (v) => {
  if (v) {
    loadPluginState();
    startRefresh();
  } else {
    stopRefresh();
  }
});
watch(() => props.proxy?.proxy_id, () => {
  // New proxy: reset to page 1 and reload with the new id.
  auditPage.value = 1;
  editingAssignment.value = false;
  editProxyUserID.value = props.proxy?.proxy_user_id ?? null;
  if (props.visible) {
    reloadAudits(0);
  }
});
watch(() => props.proxy?.node_id, (id) => {
  if (id && props.visible) loadPluginState();
});
onUnmounted(stopRefresh);

const statusColor = computed(() => {
  switch (props.proxy?.status) {
    case 'active': return 'green';
    case 'creating': return 'blue';
    case 'expired': return 'orange';
    case 'deleted': return 'red';
    default: return 'default';
  }
});

const childRunning = computed(() => Boolean(props.proxy?.child_pid && props.proxy.child_pid > 0));

const allConnColumns = [
  { title: '归属地', key: 'source_location', width: 150 },
  { title: '角色名', key: 'role_name', width: 120 },
  { title: '等级', key: 'level', width: 80 },
  { title: '游戏服', dataIndex: 'game_server', key: 'game_server', width: 120 },
  { title: '地图', key: 'map', width: 120 },
  { title: '时长', key: 'duration', width: 80 },
  { title: '代发心跳', dataIndex: 'synthetic_heartbeats_up', key: 'synthetic_heartbeats_up', width: 90 },
  { title: '流量 / 速率', key: 'bytes', width: 150 },
  { title: '背包', key: 'inventory', width: 90, fixed: 'right' },
  { title: '连接状态', key: 'connection_health', width: 145, fixed: 'right' },
];
const connColumns = computed(() => characterPluginEnabled.value ? allConnColumns : allConnColumns.filter(v => !['level','inventory'].includes(v.key)));
const inventoryColumns = [
  { title: '格子', key: 'slot', width: 70 },
  { title: '物品', key: 'name' },
  { title: '数量', dataIndex: 'quantity', width: 90 },
  { title: '属性', key: 'attribute', width: 220 },
];
const allAuditColumns = [
  { title: '结束时间', dataIndex: 'ended_at', key: 'ended_at', width: 160 },
  { title: '账号 / 角色', key: 'role', width: 150 },
  { title: '一级责任', key: 'responsibility', width: 110 },
  { title: '二级原因', key: 'reason', width: 180 },
  { title: '链路', key: 'target', width: 250 },
  { title: '时长', dataIndex: 'duration_ms', key: 'duration_ms', width: 100 },
  { title: '代发心跳', dataIndex: 'synthetic_heartbeats_up', key: 'synthetic_heartbeats_up', width: 90 },
  { title: '流量', key: 'bytes' },
];
const auditColumns = computed(() => filterAuditColumnsForCharacterPlugin(allAuditColumns, characterPluginEnabled.value));

const formatBytes = formatTrafficBytes;

const formatRate = formatTrafficRate;

const serverProbeStatus = (record: LiveConn) => {
  const stats = record.game_stats;
  if (!stats?.server_probe_requests) {
    return {
      label: '正在检测', color: 'blue', latency: '',
      detail: '正在等待游戏服务器发起连接检测，通常约一分钟内会有结果。',
    };
  }

  if (stats.probe_pending) {
    const clientAnswered = stats.probe_client_responded;
    if (!clientAnswered && stats.probe_pending_ms >= 10_000) {
      return {
        label: '用户本地网络无响应', color: 'red', latency: `等待 ${formatProbeWait(stats.probe_pending_ms)}`,
        detail: '服务器的检测请求已经到达节点，但用户本地网络一直没有回应。',
      };
    }
    if (clientAnswered) {
      return {
        label: '等待服务器确认', color: stats.probe_pending_ms >= 2000 ? 'orange' : 'gold',
        latency: stats.last_client_probe_latency_ms ? `用户本地网络 ${stats.last_client_probe_latency_ms}ms` : '',
        detail: stats.probe_pending_ms >= 2000
          ? '用户本地网络已经回应，但游戏服务器确认较慢，正在自动重试。'
          : '用户本地网络已经回应，正在等待游戏服务器确认。',
      };
    }
    return {
      label: '检测中', color: 'gold', latency: '',
      detail: '服务器的检测请求已经到达，正在等待用户本地网络回应。',
    };
  }

  const ackAge = stats.last_server_probe_ack_at_ms > 0
    ? Date.now() - stats.last_server_probe_ack_at_ms
    : Number.MAX_SAFE_INTEGER;
  if (stats.server_probe_acks > 0 && ackAge <= 120_000) {
    return {
      label: '通信正常', color: 'green',
      latency: stats.last_client_probe_latency_ms ? `用户本地网络 ${stats.last_client_probe_latency_ms}ms` : '',
      detail: `游戏服务器、节点服务器和用户本地网络已完成双向确认${stats.last_server_probe_ack_latency_ms ? `，服务器确认 ${stats.last_server_probe_ack_latency_ms}ms` : ''}。`,
    };
  }

  return {
    label: '长时间未检测', color: 'orange', latency: '',
    detail: '超过两分钟没有收到完整的服务器检测结果，请留意服务器线路或远端网络。',
  };
};

const agentProbeStatus = (record: LiveConn) => {
  const stats = record.game_stats;
  if (!stats?.agent_probe_requests) {
    return {
      key: 'agent', label: '主动请求 等待中', color: 'blue', latency: '',
      detail: '等待 Agent 主动读取传书列表，以验证节点到游戏服务器的业务请求可以正常处理。',
    };
  }
  if (stats.agent_probe_pending) {
    const timedOut = stats.agent_probe_pending_ms >= 10_000;
    return {
      key: 'agent', label: timedOut ? '主动请求 无响应' : '主动请求 检测中',
      color: timedOut ? 'red' : 'gold', latency: timedOut ? formatProbeWait(stats.agent_probe_pending_ms) : '',
      detail: timedOut
        ? 'Agent 已发送传书列表请求，但游戏服务器超过 10 秒没有返回。'
        : 'Agent 已发送无操作副作用的传书列表请求，正在等待游戏服务器返回。',
    };
  }
  const age = stats.last_agent_probe_response_at_ms > 0
    ? Date.now() - stats.last_agent_probe_response_at_ms
    : Number.MAX_SAFE_INTEGER;
  if (stats.agent_probe_responses > 0 && age <= 90_000) {
    return {
      key: 'agent', label: '主动请求 正常', color: 'green',
      latency: `${stats.last_agent_probe_latency_ms}ms`,
      detail: 'Agent 主动读取传书列表成功，说明节点到游戏服务器的业务请求链路正常。',
    };
  }
  return {
    key: 'agent', label: '主动请求 待复测', color: 'orange', latency: '',
    detail: '最近一次成功的主动业务检测已超过 90 秒，正在等待下一轮。',
  };
};

const clientHeartbeatStatus = (record: LiveConn) => {
  const stats = record.game_stats;
  if (!stats?.client_heartbeats || !stats.last_client_heartbeat_at_ms) {
    return {
      key: 'client', label: '用户本地网络心跳 等待中', color: 'blue', latency: '',
      detail: '尚未观察到用户本地连接主动发出的心跳包。',
    };
  }
  const age = Math.max(0, Date.now() - stats.last_client_heartbeat_at_ms);
  if (age <= 20_000) {
    return {
      key: 'client', label: '用户本地网络心跳 正常', color: 'green',
      latency: stats.last_client_heartbeat_interval_ms ? `间隔 ${formatProbeWait(stats.last_client_heartbeat_interval_ms)}` : '',
      detail: '持续收到用户本地连接的真实心跳，证明用户本地网络到节点服务器的上行链路正常。',
    };
  }
  if (age <= 35_000) {
    return {
      key: 'client', label: '用户本地网络心跳 变慢', color: 'orange', latency: `${formatProbeWait(age)}前`,
      detail: '用户本地网络心跳到达间隔变长，说明本地上行网络可能正在抖动。',
    };
  }
  return {
    key: 'client', label: '用户本地网络心跳 中断', color: 'red', latency: `${formatProbeWait(age)}前`,
    detail: '超过 35 秒没有收到用户本地连接的真实心跳；Agent 代发心跳不计入此项。',
  };
};

const connectionHealth = (record: LiveConn) => [
  { key: 'server', ...serverProbeStatus(record), label: `游戏服务器探测 ${serverProbeStatus(record).label}` },
  agentProbeStatus(record),
  clientHeartbeatStatus(record),
];

const connectionHealthSummary = (record: LiveConn) => {
  const items = connectionHealth(record);
  const severity: Record<string, number> = { red: 5, orange: 4, gold: 3, blue: 2, green: 1 };
  const worst = items.reduce((result, item) => (severity[item.color] || 0) > (severity[result.color] || 0) ? item : result, items[0]);
  const labels: Record<string, string> = { red: '连接异常', orange: '需要留意', gold: '检测中', blue: '等待检测', green: '连接正常' };
  return { color: worst.color, label: labels[worst.color] || '检测中', latency: worst.latency };
};

const formatProbeWait = (ms: number) => `${Math.max(1, Math.floor(ms / 1000))}秒`;

const formatDuration = (iso: string) => {
  if (!iso) return '';
  const start = new Date(iso).getTime();
  const ms = Date.now() - start;
  if (ms < 60_000) return `${Math.floor(ms / 1000)}秒`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}分`;
  return `${Math.floor(ms / 3600_000)}小时${Math.floor((ms % 3600_000) / 60_000)}分`;
};

const formatDurationMs = (ms: number) => {
  if (!ms || ms <= 0) return '0秒';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}小时${minutes}分`;
  if (minutes > 0) return `${minutes}分${seconds}秒`;
  return `${seconds}秒`;
};

const closeSideLabel = (side: string) => {
  switch (side) {
    case 'client': return '用户本地网络';
    case 'target': return '游戏服务器';
    case 'agent': return '节点服务器';
    default: return '未知';
  }
};

const closeReasonLabel = (reason: string) => {
  switch (reason) {
    case 'client_eof': return '用户本地网络主动断开（EOF）';
    case 'target_eof': return '游戏服务器主动断开（EOF）';
    case 'client_idle_timeout': return '用户本地网络空闲超时';
    case 'target_idle_timeout': return '游戏服务器空闲超时';
    case 'client_read_error': return '用户本地网络读取异常';
    case 'target_read_error': return '游戏服务器读取异常';
    case 'client_write_fail': return '写回用户本地网络失败';
    case 'target_write_fail': return '写入游戏服务器失败';
    case 'auth_fail': return '认证失败（账号/密码）';
    case 'handshake_timeout': return '握手超时';
    case 'connect_request_timeout': return 'CONNECT 请求超时';
    case 'target_connect_timeout': return '连接游戏服务器超时';
    case 'target_connect_fail': return '连接游戏服务器失败';
    case 'blacklist_blocked': return '被跨区黑名单拦截';
    case 'normal_close': return '正常关闭';
    case 'io_error': return '网络 I/O 异常';
    default: return reason || '未知原因';
  }
};

const closeSideTooltip = (side: string) => {
  switch (side) {
    case 'client': return '用户本地这一侧先断开，通常是用户本地网络或宽带线路先结束连接。';
    case 'target': return '游戏服务器这一侧先断开，通常是目标服务器拒绝、超时、维护、踢线，或者到游戏服务器的线路异常。';
    case 'agent': return '节点服务器侧主动处理，通常是Agent规则拦截、维护、超时或转发保护逻辑关闭连接。';
    default: return '系统没有判断出是哪一侧先断，只能看错误明细进一步判断。';
  }
};

const closeReasonTooltip = (reason: string) => {
  switch (reason) {
    case 'client_eof':
      return '用户本地一侧把连接关掉了。常见情况是玩家退出游戏，或者用户本地网络断开。';
    case 'target_eof':
      return '游戏服务器把连接正常关掉了。常见情况是服务器踢线、维护、主动结束连接，或者目标服那边不再继续通信。';
    case 'client_idle_timeout':
      return '用户本地网络长时间没有发数据，节点服务器判断连接空闲太久后关闭。';
    case 'target_idle_timeout':
      return '游戏服务器长时间没有返回数据，节点判断游戏服务器侧空闲太久后关闭。';
    case 'client_read_error':
      return '节点服务器从用户本地网络读取数据时失败。常见是本地网络突然断开，或宽带连接被重置。';
    case 'target_read_error':
      return '节点从游戏服务器读取数据时失败。常见是游戏服务器连接异常中断，或者到游戏服务器的网络线路断了。';
    case 'client_write_fail':
      return '节点服务器想把游戏服务器数据发回用户本地网络，但已经无法写入。常见是用户本地网络中断或宽带连接异常。';
    case 'target_write_fail':
      return '节点服务器想把用户本地数据发给游戏服务器，但游戏服务器方向已经不可写。常见是目标服断开或到目标服线路异常。';
    case 'auth_fail':
      return '连接进来了，但没有通过 SOCKS5 认证。常见是账号密码配置错、非 SOCKS5 流量打进来，或者外部扫端口。';
    case 'handshake_timeout':
      return '用户本地连接没有在规定时间内完成握手，通常是本地网络异常或配置错误。';
    case 'connect_request_timeout':
      return '用户本地连接建立后，没有及时发出游戏服务器连接请求，通常是本地网络异常或连接流程不完整。';
    case 'target_connect_timeout':
      return '节点去连接游戏服务器，但 10 秒内没有连上。重点看游戏服务器是否可达、是否维护，或者节点到游戏服线路是否慢。';
    case 'target_connect_fail':
      return '节点连接游戏服务器时直接失败。通常是目标地址拒绝连接、目标端口不通，或者到游戏服务器的网络不可达。';
    case 'blacklist_blocked':
      return '节点按规则拦截了这个目标，通常是访问到了不属于当前大区的服务器。这是规则生效，不是故障掉线。';
    case 'normal_close':
      return '连接按正常流程结束，没有明显异常。';
    case 'io_error':
      return '底层网络读写发生异常，但系统无法明确归到用户本地网络还是游戏服务器，需要结合错误明细和时间点判断。';
    case 'idle_timeout':
      return '连接长时间没有有效数据，节点按空闲超时规则关闭。';
    default:
      return '暂时没有内置解释，请结合原始原因和错误明细判断。';
  }
};

// Backend ISO 8601 with offset → compact operator-friendly datetime.
const formatDateTime = (s?: string) => {
  if (!s) return '';
  return s.replace('T', ' ').replace(/\+\d{2}:\d{2}$|Z$/, '');
};

const formatChildStartedAt = (startedAt?: number | null) => {
  if (!startedAt || startedAt <= 0) return '未知';
  return formatDateTime(new Date(startedAt * 1000).toISOString());
};

const formatChildUptime = (startedAt?: number | null) => {
  if (!startedAt || startedAt <= 0) return '未知';
  const seconds = Math.floor(Date.now() / 1000) - startedAt;
  if (seconds < 0) return '时钟异常';
  if (seconds < 60) return `${seconds}秒`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) return remainingMinutes ? `${hours}小时${remainingMinutes}分` : `${hours}小时`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days}天${remainingHours}小时` : `${days}天`;
};

const copyField = async (text: string, label: string) => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label}已复制`);
  } catch {
    message.error('复制失败');
  }
};

const copyAll = async () => {
  const p = props.proxy;
  if (!p) return;
  const ip = await resolveNodeIP(p.node_id);
  if (!ip) return;
  const joined = [ip, String(p.port), p.username, p.password, p.expire_at?.slice(0, 10) || ''].join('|');
  await copyField(joined, '5 字段');
};

const copyByLines = async () => {
  const p = props.proxy;
  if (!p) return;
  const ip = await resolveNodeIP(p.node_id);
  if (!ip) return;
  const lines = [
    `IP：${ip || '-'}`,
    `端口：${p.port}`,
    `账号：${p.username}`,
    `密码：${p.password}`,
    `有效期：${p.expire_at?.slice(0, 10) || ''}`,
  ].join('\n');
  await copyField(lines, '按行信息');
};

const resolveNodeIP = async (nodeID: number) => {
  try {
    const r = await resolveNodeAddress(nodeID);
    return r.data.resolved_ip;
  } catch (e: any) {
    message.error(e.response?.data?.error?.message || '节点域名解析失败，未复制配置');
    return '';
  }
};
</script>

<style scoped>
.connection-health-compact {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: help;
  white-space: nowrap;
}
.connection-health-latency {
  color: #6b7280;
  font-size: 11px;
  white-space: nowrap;
}
.connection-health-detail { width: 360px; line-height: 1.5; }
.connection-health-detail > div + div { margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,.18); }
.connection-health-detail div div { color: rgba(255,255,255,.78); font-size: 12px; }
.potion-config { margin-bottom: 14px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fafafa; }
.resource-summary { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:12px; }
.resource-summary > div { display:flex; justify-content:space-between; padding:10px 12px; border-radius:8px; background:#f8fafc; }
.resource-summary span { color:#6b7280; }.hp-value { color:#cf1322; }.mp-value { color:#1677ff; }
.potion-config-title, .potion-config-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.potion-config-grid { display: grid; grid-template-columns: 72px 145px minmax(220px, 1fr); gap: 10px; align-items: center; margin: 12px 0; }
.potion-status { color: #6b7280; font-size: 12px; }
.role-detail-link { padding: 0; border: 0; background: transparent; color: #1677ff; font: inherit; font-weight: 600; cursor: pointer; }
.role-detail-link:hover { color: #4096ff; text-decoration: underline; }
.traffic-metric { display: block; font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
.traffic-rate { color: #9ca3af; font-size: 11px; }
.plugin-list { display: grid; gap: 12px; }
.plugin-item { border-radius: 10px; }
.plugin-item-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.plugin-item p { margin: 10px 0 6px; color: #6b7280; line-height: 1.6; }
.plugin-item small { color: #8c8c8c; }
</style>
